import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { comma } from "../constants";
import Card from "./ui/Card";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Btn from "./ui/Btn";
import Modal from "./ui/Modal";
import EmptyState from "./ui/EmptyState";
import DeficitCasePrint from "./DeficitCasePrint";

export default function DeficitSection({ reviewers, stations, toast, user }) {
  const [cases, setCases] = useState([]);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [printCase, setPrintCase] = useState(null);
  const [editCase, setEditCase] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const isAdmin = user?.role === "admin";
  const isReadOnly = user?.role === "auditor";
  const [form, setForm] = useState({
    station_id: "",
    reviewer_id: "",
    supervisor_name: "",
    supervisor_phone: "",
    reply_datetime: "",
    reply_load: "",
    reply_value: "",
    notes: "",
  });
  const [step, setStep] = useState({
    validity_confirmed: null,
    validity_reason: "",
    contact_done: null,
    supervisor_response: "",
    memo_number: "",
  });

  const load = useCallback(async () => {
    try {
      const r = await api(
        `/deficit${filterStatus ? `?status=${filterStatus}` : ""}`
      );
      setCases(r || []);
    } catch {}
  }, [filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const createCase = async () => {
    if (
      !form.station_id ||
      !form.reviewer_id ||
      !form.supervisor_name ||
      !form.reply_datetime
    ) {
      toast("يرجى ملء الحقول المطلوبة", "warning");
      return;
    }
    try {
      await api("/deficit", {
        method: "POST",
        body: {
          ...form,
          station_id: parseInt(form.station_id),
          reviewer_id: parseInt(form.reviewer_id),
          reply_value: form.reply_value ? parseFloat(form.reply_value) : null,
        },
      });
      toast("✅ تم إضافة حالة العجز");
      setModal(null);
      load();
    } catch (e) {
      toast("خطأ: " + e.message, "error");
    }
  };

  const updateStep = async (did, stepName, data) => {
    try {
      await api(`/deficit/${did}`, {
        method: "PUT",
        body: { step: stepName, ...data },
      });
      toast("✅ تم التحديث");
      const updated = await api(`/deficit/${did}`);
      if (updated) {
        setSelected(updated);
        setCases((prev) =>
          prev.map((c) => (c.id === updated.id ? updated : c))
        );
      }
    } catch (e) {
      toast("خطأ: " + e.message, "error");
    }
  };

  const saveCaseDetails = async () => {
    if (
      !editCase.station_id ||
      !editCase.reviewer_id ||
      !editCase.supervisor_name ||
      !editCase.reply_datetime
    ) {
      toast("يرجى ملء الحقول المطلوبة", "warning");
      return;
    }
    await updateStep(selected.id, "details", {
      station_id: parseInt(editCase.station_id),
      reviewer_id: parseInt(editCase.reviewer_id),
      supervisor_name: editCase.supervisor_name,
      supervisor_phone: editCase.supervisor_phone || null,
      reply_datetime: editCase.reply_datetime,
      reply_load: editCase.reply_load || null,
      reply_value: editCase.reply_value ? parseFloat(editCase.reply_value) : null,
      notes: editCase.notes || null,
    });
    setEditCase(null);
  };

  const deleteCase = async (did) => {
    if (!window.confirm("حذف حالة العجز هذه نهائيًا؟ لا يمكن التراجع عن هذا الإجراء."))
      return;
    try {
      await api(`/deficit/${did}`, { method: "DELETE" });
      toast("🗑️ تم حذف الحالة");
      setSelected(null);
      setCases((prev) => prev.filter((c) => c.id !== did));
    } catch (e) {
      toast("خطأ: " + e.message, "error");
    }
  };

  const statusColor = { open: "#f59e0b", closed: "#22c55e" };
  const statusLabel = { open: "مفتوح", closed: "مغلق" };

  const isSet = (v) => v !== null && v !== undefined;

  const stepStatus = (c) => {
    if (c.memo_number)
      return { label: "مذكرة أُعدت", color: "#22c55e", step: 4 };
    if (c.supervisor_response)
      return { label: "استجاب المشرف", color: "#38bdf8", step: 3 };
    if (isSet(c.contact_done))
      return {
        label: c.contact_done ? "تم التواصل" : "لم يتم التواصل",
        color: c.contact_done ? "#a78bfa" : "#ef4444",
        step: 2,
      };
    if (isSet(c.validity_confirmed))
      return {
        label: c.validity_confirmed ? "الرد صحيح" : "الرد غير صحيح",
        color: c.validity_confirmed ? "#22c55e" : "#ef4444",
        step: 1,
      };
    return { label: "في الانتظار", color: "#64748b", step: 0 };
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 20,
              fontWeight: 800,
              margin: 0,
              color: "#f1f5f9",
            }}
          >
            ⚠️ عجز الردود
          </h2>
          <p style={{ color: "#64748b", fontSize: 13, margin: "4px 0 0" }}>
            إدارة حالات الردود غير المُبلَّغ عنها للمالية
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ minWidth: 140 }}>
            <Select
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="الكل"
              options={[
                { value: "open", label: "مفتوح" },
                { value: "closed", label: "مغلق" },
              ]}
            />
          </div>
          {!isReadOnly && (
            <Btn
              onClick={() => {
                setForm({
                  station_id: "",
                  reviewer_id: "",
                  supervisor_name: "",
                  supervisor_phone: "",
                  reply_datetime: "",
                  reply_load: "",
                  reply_value: "",
                  notes: "",
                });
                setModal("add");
              }}
              color="#ef4444"
            >
              + إضافة حالة عجز
            </Btn>
          )}
        </div>
      </div>

      {cases.length === 0 ? (
        <Card>
          <EmptyState
            message={`لا توجد حالات عجز ${
              filterStatus ? `(${statusLabel[filterStatus]})` : ""
            }`}
          />
        </Card>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(380px,1fr))",
            gap: 14,
          }}
        >
          {cases.map((c) => {
            const { label, color, step: st } = stepStatus(c);
            const respRefId = `resp_${c.id}`;
            return (
              <Card
                key={c.id}
                style={{
                  padding: "18px 20px",
                  cursor: "pointer",
                  border: `1px solid ${color}33`,
                }}
                onClick={() => setSelected(c)}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#f1f5f9",
                        fontWeight: 700,
                        fontSize: 15,
                      }}
                    >
                      {c.station_name}
                    </div>
                    <div
                      style={{
                        color: "#64748b",
                        fontSize: 12,
                        marginTop: 2,
                      }}
                    >
                      مراجع: {c.reviewer_name?.split(" ")[0]}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      alignItems: "flex-end",
                    }}
                  >
                    <span
                      style={{
                        background: `${statusColor[c.status]}22`,
                        color: statusColor[c.status],
                        borderRadius: 6,
                        padding: "2px 8px",
                        fontSize: 11,
                        border: `1px solid ${statusColor[c.status]}44`,
                      }}
                    >
                      {statusLabel[c.status]}
                    </span>
                    <span
                      style={{
                        background: `${color}22`,
                        color,
                        borderRadius: 6,
                        padding: "2px 8px",
                        fontSize: 10,
                      }}
                    >
                      {label}
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      background: "#0f172a",
                      borderRadius: 8,
                      padding: "8px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "#f1f5f9",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {c.supervisor_name}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 10 }}>
                      مشرف المحطة
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#0f172a",
                      borderRadius: 8,
                      padding: "8px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        color: "#38bdf8",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {c.reply_datetime?.split("T")[0] ||
                        c.reply_datetime?.split(" ")[0]}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 10 }}>
                      تاريخ الرد
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {["التحقق", "التواصل", "رد المشرف", "المذكرة"].map(
                    (s, i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: 4,
                          borderRadius: 99,
                          background: st > i ? color : "#1e293b",
                          transition: "background .3s",
                        }}
                      />
                    )
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: 6,
                  }}
                >
                  <span style={{ color: "#64748b", fontSize: 10 }}>
                    الخطوة {st}/4
                  </span>
                  {c.reply_value && (
                    <span
                      style={{
                        color: "#f59e0b",
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      {comma(c.reply_value)} ر.س
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Modal */}
      {modal === "add" && (
        <Modal
          title="إضافة حالة عجز جديدة"
          onClose={() => setModal(null)}
          wide
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            <Select
              label="المحطة"
              value={form.station_id}
              onChange={(v) => setForm((f) => ({ ...f, station_id: v }))}
              options={stations.map((s) => ({
                value: s.id,
                label: s.name,
              }))}
              required
            />
            <Select
              label="المراجع"
              value={form.reviewer_id}
              onChange={(v) => setForm((f) => ({ ...f, reviewer_id: v }))}
              options={reviewers.map((r) => ({
                value: r.id,
                label: r.name,
              }))}
              required
            />
            <Input
              label="اسم مشرف المحطة"
              value={form.supervisor_name}
              onChange={(v) => setForm((f) => ({ ...f, supervisor_name: v }))}
              required
            />
            <Input
              label="جوال المشرف"
              value={form.supervisor_phone}
              onChange={(v) => setForm((f) => ({ ...f, supervisor_phone: v }))}
            />
            <Input
              label="تاريخ ووقت الرد"
              type="datetime-local"
              value={form.reply_datetime}
              onChange={(v) => setForm((f) => ({ ...f, reply_datetime: v }))}
              required
            />
            <Input
              label="حمولة الرد"
              value={form.reply_load}
              onChange={(v) => setForm((f) => ({ ...f, reply_load: v }))}
              placeholder="مثال: 15 طن"
            />
            <Input
              label="قيمة الرد (ر.س)"
              type="number"
              value={form.reply_value}
              onChange={(v) => setForm((f) => ({ ...f, reply_value: v }))}
            />
            <div />
            <div style={{ gridColumn: "1/-1" }}>
              <Input
                label="ملاحظات"
                value={form.notes}
                onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
                placeholder="تفاصيل إضافية..."
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <Btn onClick={createCase} color="#ef4444" size="lg">
              ➕ إضافة الحالة
            </Btn>
            <Btn onClick={() => setModal(null)} color="#64748b" size="lg">
              إلغاء
            </Btn>
          </div>
        </Modal>
      )}

      {/* Edit Case Details Modal */}
      {editCase && (
        <Modal
          title="تعديل بيانات حالة العجز"
          onClose={() => setEditCase(null)}
          wide
          zIndex={1100}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            <Select
              label="المحطة"
              value={editCase.station_id}
              onChange={(v) => setEditCase((f) => ({ ...f, station_id: v }))}
              options={stations.map((s) => ({
                value: s.id,
                label: s.name,
              }))}
              required
            />
            <Select
              label="المراجع"
              value={editCase.reviewer_id}
              onChange={(v) => setEditCase((f) => ({ ...f, reviewer_id: v }))}
              options={reviewers.map((r) => ({
                value: r.id,
                label: r.name,
              }))}
              required
            />
            <Input
              label="اسم مشرف المحطة"
              value={editCase.supervisor_name}
              onChange={(v) => setEditCase((f) => ({ ...f, supervisor_name: v }))}
              required
            />
            <Input
              label="جوال المشرف"
              value={editCase.supervisor_phone}
              onChange={(v) => setEditCase((f) => ({ ...f, supervisor_phone: v }))}
            />
            <Input
              label="تاريخ ووقت الرد"
              type="datetime-local"
              value={editCase.reply_datetime}
              onChange={(v) => setEditCase((f) => ({ ...f, reply_datetime: v }))}
              required
            />
            <Input
              label="حمولة الرد"
              value={editCase.reply_load}
              onChange={(v) => setEditCase((f) => ({ ...f, reply_load: v }))}
              placeholder="مثال: 15 طن"
            />
            <Input
              label="قيمة الرد (ر.س)"
              type="number"
              value={editCase.reply_value}
              onChange={(v) => setEditCase((f) => ({ ...f, reply_value: v }))}
            />
            <div />
            <div style={{ gridColumn: "1/-1" }}>
              <Input
                label="ملاحظات"
                value={editCase.notes}
                onChange={(v) => setEditCase((f) => ({ ...f, notes: v }))}
                placeholder="تفاصيل إضافية..."
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <Btn onClick={saveCaseDetails} color="#22c55e" size="lg">
              💾 حفظ التعديلات
            </Btn>
            <Btn onClick={() => setEditCase(null)} color="#64748b" size="lg">
              إلغاء
            </Btn>
          </div>
        </Modal>
      )}

      {/* Case Detail Modal */}
      {selected && (
        <Modal
          title={`تفاصيل حالة العجز — ${selected.station_name}`}
          onClose={() => setSelected(null)}
          wide
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn size="sm" color="#38bdf8" onClick={() => setPrintCase(selected)}>
                🖨️ طباعة
              </Btn>
              {!isReadOnly && (
                <Btn
                  size="sm"
                  color="#f59e0b"
                  onClick={() =>
                    setEditCase({
                      station_id: String(selected.station_id),
                      reviewer_id: String(selected.reviewer_id),
                      supervisor_name: selected.supervisor_name || "",
                      supervisor_phone: selected.supervisor_phone || "",
                      reply_datetime: (selected.reply_datetime || "")
                        .replace(" ", "T")
                        .slice(0, 16),
                      reply_load: selected.reply_load || "",
                      reply_value:
                        selected.reply_value != null ? String(selected.reply_value) : "",
                      notes: selected.notes || "",
                    })
                  }
                >
                  ✏️ تعديل البيانات
                </Btn>
              )}
              {isAdmin && (
                <Btn size="sm" color="#ef4444" onClick={() => deleteCase(selected.id)}>
                  🗑️ حذف الحالة
                </Btn>
              )}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: 10,
              }}
            >
              {[
                ["المحطة", selected.station_name, "#38bdf8"],
                [
                  "المراجع",
                  (selected.reviewer_name || "")
                    .split(" ")
                    .slice(0, 2)
                    .join(" "),
                  "#a78bfa",
                ],
                ["مشرف المحطة", selected.supervisor_name, "#f59e0b"],
                [
                  "جوال المشرف",
                  selected.supervisor_phone || "—",
                  "#64748b",
                ],
                [
                  "تاريخ الرد",
                  selected.reply_datetime?.split("T")[0] ||
                    selected.reply_datetime ||
                    "—",
                  "#06b6d4",
                ],
                [
                  "قيمة الرد",
                  selected.reply_value
                    ? `${comma(selected.reply_value)} ر.س`
                    : "—",
                  "#22c55e",
                ],
              ].map(([l, v, c]) => (
                <div
                  key={l}
                  style={{
                    background: "#0f172a",
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div style={{ color: "#64748b", fontSize: 11 }}>{l}</div>
                  <div
                    style={{
                      color: c,
                      fontWeight: 700,
                      fontSize: 13,
                      marginTop: 2,
                    }}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </div>

            {/* Step 1 */}
            <div
              style={{
                background: "#0f172a",
                borderRadius: 12,
                padding: 16,
                border: `1px solid ${
                  isSet(selected.validity_confirmed)
                    ? "#22c55e44"
                    : "#334155"
                }`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    color: "#f1f5f9",
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  1️⃣ التحقق من صحة الرد
                </div>
                {isSet(selected.validity_confirmed) && (
                  <span
                    style={{
                      color: selected.validity_confirmed
                        ? "#22c55e"
                        : "#ef4444",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {selected.validity_confirmed ? "✅ صحيح" : "❌ غير صحيح"}
                  </span>
                )}
              </div>
              {!isSet(selected.validity_confirmed) ? (
                isReadOnly ? (
                  <div style={{ color: "#64748b", fontSize: 13 }}>لم يُنفَّذ بعد</div>
                ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn
                    color="#22c55e"
                    onClick={() =>
                      updateStep(selected.id, "validity_check", {
                        validity_confirmed: true,
                      })
                    }
                  >
                    ✅ نعم — الرد صحيح
                  </Btn>
                  <Btn
                    color="#ef4444"
                    onClick={() => {
                      const reason = window.prompt(
                        "أذكر سبب عدم صحة الرد:"
                      );
                      if (reason)
                        updateStep(selected.id, "validity_check", {
                          validity_confirmed: false,
                          validity_reason: reason,
                        });
                    }}
                  >
                    ❌ لا — غير صحيح
                  </Btn>
                </div>
                )
              ) : (
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  {selected.validity_reason || "تم التحقق"}
                </div>
              )}
            </div>

            {/* Step 2 */}
            <div
              style={{
                background: "#0f172a",
                borderRadius: 12,
                padding: 16,
                border: `1px solid ${
                  isSet(selected.contact_done) ? "#a78bfa44" : "#334155"
                }`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    color: "#f1f5f9",
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  2️⃣ التواصل مع المشرف ومشاركة الصور
                </div>
                {isSet(selected.contact_done) && (
                  <span
                    style={{
                      color: selected.contact_done ? "#22c55e" : "#ef4444",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {selected.contact_done ? "✅ تم" : "❌ لم يتم"}
                  </span>
                )}
              </div>
              {!isSet(selected.contact_done) && (
                isReadOnly ? (
                  <div style={{ color: "#64748b", fontSize: 13 }}>لم يُنفَّذ بعد</div>
                ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <Btn
                    color="#22c55e"
                    onClick={() =>
                      updateStep(selected.id, "contact", {
                        contact_done: true,
                      })
                    }
                  >
                    ✅ تم التواصل
                  </Btn>
                  <Btn
                    color="#ef4444"
                    onClick={() =>
                      updateStep(selected.id, "contact", {
                        contact_done: false,
                      })
                    }
                  >
                    ❌ لم يتم
                  </Btn>
                </div>
                )
              )}
            </div>

            {/* Step 3 */}
            <div
              style={{
                background: "#0f172a",
                borderRadius: 12,
                padding: 16,
                border: `1px solid ${
                  selected.supervisor_response ? "#38bdf844" : "#334155"
                }`,
              }}
            >
              <div
                style={{
                  color: "#f1f5f9",
                  fontWeight: 700,
                  fontSize: 14,
                  marginBottom: 10,
                }}
              >
                3️⃣ رد المشرف
              </div>
              {selected.supervisor_response ? (
                <div>
                  <div
                    style={{
                      color: "#cbd5e1",
                      fontSize: 13,
                      lineHeight: 1.7,
                      background: "#1e293b",
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 8,
                    }}
                  >
                    {selected.supervisor_response}
                  </div>
                  {!isReadOnly && (
                    <Btn
                      size="sm"
                      color="#64748b"
                      onClick={() =>
                        updateStep(selected.id, "supervisor_response", {
                          supervisor_response: "",
                        })
                      }
                    >
                      ✏️ تعديل
                    </Btn>
                  )}
                </div>
              ) : isReadOnly ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>لم يُدرج رد بعد</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <textarea
                    placeholder="أدخل رد المشرف هنا..."
                    id={`resp_${selected.id}`}
                    rows={4}
                    style={{
                      background: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: 8,
                      padding: "10px 12px",
                      color: "#f1f5f9",
                      fontSize: 13,
                      fontFamily: "inherit",
                      outline: "none",
                      resize: "vertical",
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  />
                  <Btn
                    color="#38bdf8"
                    onClick={() => {
                      const v = document.getElementById(`resp_${selected.id}`)
                        ?.value;
                      if (v)
                        updateStep(selected.id, "supervisor_response", {
                          supervisor_response: v,
                        });
                    }}
                  >
                    حفظ الرد
                  </Btn>
                </div>
              )}
            </div>

            {/* Step 4 */}
            <div
              style={{
                background: "#0f172a",
                borderRadius: 12,
                padding: 16,
                border: `1px solid ${
                  selected.memo_number ? "#22c55e44" : "#334155"
                }`,
              }}
            >
              <div
                style={{
                  color: "#f1f5f9",
                  fontWeight: 700,
                  fontSize: 14,
                  marginBottom: 10,
                }}
              >
                4️⃣ إعداد مذكرة بالواقعة
              </div>
              {selected.memo_number ? (
                <div>
                  <div
                    style={{
                      color: "#22c55e",
                      fontWeight: 700,
                      fontSize: 14,
                      marginBottom: 8,
                    }}
                  >
                    رقم المذكرة: {selected.memo_number}
                  </div>
                  {!isReadOnly && (
                    <Btn
                      size="sm"
                      color="#64748b"
                      onClick={() =>
                        updateStep(selected.id, "memo", { memo_number: "" })
                      }
                    >
                      ✏️ تعديل
                    </Btn>
                  )}
                </div>
              ) : isReadOnly ? (
                <div style={{ color: "#64748b", fontSize: 13 }}>لم تُعد بعد</div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-end",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Input
                      label="رقم المذكرة"
                      id={`memo_${selected.id}`}
                      value={step.memo_number}
                      onChange={(v) =>
                        setStep((s) => ({ ...s, memo_number: v }))
                      }
                      placeholder="أدخل رقم المذكرة..."
                    />
                  </div>
                  <Btn
                    color="#22c55e"
                    onClick={() => {
                      if (step.memo_number)
                        updateStep(selected.id, "memo", {
                          memo_number: step.memo_number,
                        });
                    }}
                  >
                    ✅ إغلاق الحالة
                  </Btn>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {printCase && (
        <DeficitCasePrint caseData={printCase} onClose={() => setPrintCase(null)} />
      )}
    </div>
  );
}
